/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  FormControlLabel,
  Checkbox,
  Chip,
  Grid,
  Autocomplete,
  CircularProgress,
  Tooltip as MuiTooltip,
  Box,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import INDataTable from "components/INDataTable";

const ROLES = [
  { value: "VIEW", label: "View Access" },
  { value: "CATEGORY_OWNER", label: "Category Admin" },
  { value: "RISK_ADMIN", label: "Admin" },
  // SUPER_RISK_ADMIN is an internal role — not offered in the UI.
];

const ALL_CATEGORIES_OPTION = {
  categoryId: "__ALL__",
  taxonomyId: "__ALL__",
  label: "All Categories",
};

function TenantUsers() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyUser, setModifyUser] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState("VIEW");
  const [sendInvite, setSendInvite] = useState(false);
  const [categoryAttachments, setCategoryAttachments] = useState([ALL_CATEGORIES_OPTION]);
  const [modifyCategoryAttachments, setModifyCategoryAttachments] = useState([]);
  const [modifyFirstName, setModifyFirstName] = useState("");
  const [modifyLastName, setModifyLastName] = useState("");
  const [modifyRole, setModifyRole] = useState("VIEW");
  const [deleteOpen, setDeleteOpen] = useState(false); // ID211 delete-user confirmation
  const [deleteUserRow, setDeleteUserRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categoriesForAssignment, setCategoriesForAssignment] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const listFetched = useRef(false);

  const base = protectedResources?.apiTodoList?.endpoint || "";

  const loadUsers = () => {
    if (!base) return;
    execute("GET", `${base}/users/list`)
      .then((res) => {
        if (res?.data) setUsers(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (listFetched.current || !base) return;
    listFetched.current = true;
    loadUsers();
  }, [base, execute]);

  const needsCategoryPicker = (role) => role === "CATEGORY_OWNER" || role === "VIEW";

  useEffect(() => {
    if (!addOpen || !needsCategoryPicker(addRole) || !base) return;
    if (categoriesForAssignment.length > 0) return; // already loaded
    setCategoriesLoading(true);
    execute("GET", `${base}/taxonomy/categories-for-assignment`)
      .then((res) => {
        if (res?.data && Array.isArray(res.data)) {
          setCategoriesForAssignment(res.data);
        } else {
          setCategoriesForAssignment([]);
        }
      })
      .catch(() => setCategoriesForAssignment([]))
      .finally(() => setCategoriesLoading(false));
  }, [addOpen, addRole, base, execute]);

  // Also load categories when the edit dialog needs the picker (honours the edited role, so
  // switching a user TO a category role in the dialog loads the options).
  useEffect(() => {
    if (!modifyOpen || !modifyUser || !needsCategoryPicker(modifyRole) || !base) return;
    if (categoriesForAssignment.length > 0) return;
    setCategoriesLoading(true);
    execute("GET", `${base}/taxonomy/categories-for-assignment`)
      .then((res) => {
        if (res?.data && Array.isArray(res.data)) setCategoriesForAssignment(res.data);
        else setCategoriesForAssignment([]);
      })
      .catch(() => setCategoriesForAssignment([]))
      .finally(() => setCategoriesLoading(false));
  }, [modifyOpen, modifyUser, modifyRole, base, execute]);

  const handleInviteForLogin = useCallback(
    (email, onSuccess) => {
      setError(null);
      setMessage(null);
      const user = users.find((u) => (u.email || "").toLowerCase() === (email || "").toLowerCase());
      const role = user?.role || "VIEW";
      execute("POST", `${base}/users/invite`, { email: email.trim().toLowerCase(), role })
        .then((res) => {
          if (res?.error) {
            setError(res.error);
            return;
          }
          setMessage(res?.message || "Invite sent.");
          loadUsers();
          onSuccess?.();
        })
        .catch((err) => setError(err?.message || "Failed to send invite."));
    },
    [users, execute, base]
  );

  const handleResendInvite = useCallback(
    (email, onSuccess) => {
      setError(null);
      setMessage(null);
      execute("POST", `${base}/users/resend-invite`, { email: email.trim().toLowerCase() })
        .then((res) => {
          if (res?.error) {
            setError(res.error);
            return;
          }
          setMessage(res?.message || "Invite resent.");
          loadUsers();
          onSuccess?.();
        })
        .catch((err) => setError(err?.message || "Failed to resend invite."));
    },
    [execute, base]
  );

  const usersColumns = useMemo(
    () => [
      { accessorKey: "name", header: "Name", Cell: ({ row }) => row.original.name || "—" },
      {
        accessorKey: "email",
        header: "Email",
        Cell: ({ row }) => row.original.email || row.original.preferred_username || "—",
      },
      {
        accessorKey: "role",
        header: "Role",
        Cell: ({ row }) => {
          const role = row.original.role || "VIEW";
          const label = ROLES.find((r) => r.value === role)?.label || role;
          return (
            <Chip label={label} size="small" variant="outlined" sx={{ borderColor: "#B5BFD0" }} />
          );
        },
      },
      {
        accessorKey: "inviteStatus",
        header: "Invite Status",
        Cell: ({ row }) => {
          const s = row.original.inviteStatus || "accepted";
          if (s === "accepted")
            return <Chip label="Accepted" size="small" color="success" variant="outlined" />;
          if (s === "pending") {
            const exp = row.original.inviteExpiresAt
              ? new Date(row.original.inviteExpiresAt).toLocaleString()
              : null;
            if (row.original.inviteExpired) {
              return (
                <MuiTooltip title={exp ? `Invite expired ${exp}` : "Invite expired"}>
                  <Chip label="Expired" size="small" color="error" variant="outlined" />
                </MuiTooltip>
              );
            }
            return (
              <MuiTooltip title={exp ? `Invite expires ${exp}` : "Invite pending"}>
                <Chip label="Pending" size="small" color="warning" variant="outlined" />
              </MuiTooltip>
            );
          }
          return (
            <Chip label="None" size="small" variant="outlined" sx={{ borderColor: "#B5BFD0" }} />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {row.original.inviteStatus === "pending" && (
              <MuiTooltip
                title={row.original.inviteExpired ? "Invite expired — resend" : "Resend invite"}
              >
                <IconButton
                  size="small"
                  color={row.original.inviteExpired ? "error" : "warning"}
                  onClick={() => handleResendInvite(row.original.email)}
                  aria-label="Resend invite"
                >
                  <Icon fontSize="small">forward_to_inbox</Icon>
                </IconButton>
              </MuiTooltip>
            )}
            <MuiTooltip title="Modify user">
              <IconButton
                size="small"
                color="info"
                onClick={() => {
                  const u = row.original;
                  setModifyUser(u);
                  setModifyFirstName(
                    u.firstName ||
                      (u.name && !u.name.includes("@") ? u.name.split(" ")[0] : "") ||
                      ""
                  );
                  setModifyLastName(
                    u.lastName ||
                      (u.name && !u.name.includes("@")
                        ? u.name.split(" ").slice(1).join(" ")
                        : "") ||
                      ""
                  );
                  setModifyRole(u.role || "VIEW");
                  const existing = u.categoryAttachments || [];
                  setModifyCategoryAttachments(
                    existing.length === 0 && needsCategoryPicker(u.role || "VIEW")
                      ? [ALL_CATEGORIES_OPTION]
                      : existing
                  );
                  setModifyOpen(true);
                }}
                aria-label="Modify user"
              >
                <ModeEditOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
            <MuiTooltip title="Delete user">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setDeleteUserRow(row.original);
                  setDeleteOpen(true);
                }}
                aria-label="Delete user"
              >
                <Icon fontSize="small">delete_outline</Icon>
              </IconButton>
            </MuiTooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const handleAddSubmit = () => {
    setError(null);
    setMessage(null);
    if (!addEmail.trim()) {
      setError("Email is required.");
      return;
    }
    const attachments = needsCategoryPicker(addRole)
      ? categoryAttachments
          .filter((item) => item.categoryId !== "__ALL__")
          .map((item) => ({ taxonomyId: item.taxonomyId, categoryId: item.categoryId }))
      : [];
    setAddLoading(true);
    execute("POST", `${base}/users/add`, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: addEmail.trim(),
      role: addRole,
      sendInvite,
      categoryAttachments: attachments,
    })
      .then((res) => {
        if (res?.error) {
          setError(res.error);
          return;
        }
        setMessage(res?.message || "User added.");
        setAddOpen(false);
        setFirstName("");
        setLastName("");
        setAddEmail("");
        setAddRole("VIEW");
        setSendInvite(false);
        setCategoryAttachments([ALL_CATEGORIES_OPTION]);
        loadUsers();
      })
      .catch((err) => setError(err?.message || "Failed to add user."))
      .finally(() => setAddLoading(false));
  };

  const handleModifyInvite = () => {
    if (!modifyUser?.email) return;
    const s = modifyUser.inviteStatus || "accepted";
    const closeModal = () => {
      setModifyOpen(false);
      setModifyUser(null);
    };
    if (s === "none") {
      handleInviteForLogin(modifyUser.email, closeModal);
    } else if (s === "pending") {
      handleResendInvite(modifyUser.email, closeModal);
    }
  };

  const handleSaveCategories = () => {
    if (!modifyUser?.email) return;
    setError(null);
    setMessage(null);
    const email = encodeURIComponent(modifyUser.email.trim().toLowerCase());
    const attachments = modifyCategoryAttachments
      .filter((item) => item.categoryId !== "__ALL__")
      .map((item) => ({ taxonomyId: item.taxonomyId, categoryId: item.categoryId }));
    const payload = {
      firstName: modifyFirstName.trim(),
      lastName: modifyLastName.trim(),
      role: modifyRole,
    };
    if (needsCategoryPicker(modifyRole)) payload.categoryAttachments = attachments;
    execute("PUT", `${base}/users/update/${email}`, payload)
      .then((res) => {
        if (res?.error) {
          setError(res.error);
          return;
        }
        setMessage("User updated.");
        setModifyOpen(false);
        setModifyUser(null);
        loadUsers();
      })
      .catch((err) => setError(err?.message || "Failed to update."));
  };

  // ID211 — delete a user from this tenant (after the warning confirmation).
  const handleDeleteUser = () => {
    if (!deleteUserRow?.email) return;
    setDeleting(true);
    setError(null);
    setMessage(null);
    const email = encodeURIComponent(deleteUserRow.email.trim().toLowerCase());
    execute("DELETE", `${base}/users/delete/${email}`)
      .then((res) => {
        if (res?.error) {
          setError(res.error);
          return;
        }
        setMessage("User deleted.");
        setDeleteOpen(false);
        setDeleteUserRow(null);
        loadUsers();
      })
      .catch((err) => setError(err?.message || "Failed to delete user."))
      .finally(() => setDeleting(false));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox bgColor="white" padding="15px" borderRadius="8px">
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Grid item>
            <MDTypography fontWeight="medium" fontSize="21px" color="#263445" opacity={1}>
              Users
            </MDTypography>
            <MDTypography fontWeight="light" fontSize="15px" color="#8090AA" opacity={1}>
              Manage users and pending invites for your tenant.
            </MDTypography>
          </Grid>
          <Grid item>
            <MDButton
              variant="contained"
              onClick={() => setAddOpen(true)}
              sx={{
                backgroundColor: "#402899",
                color: "#fff",
                "&:hover": { backgroundColor: "#352080" },
              }}
            >
              <Icon sx={{ mr: 0.5 }}>person_add</Icon>
              Add user
            </MDButton>
          </Grid>
        </Grid>

        {message && (
          <Alert severity="success" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <hr style={{ border: "none", borderTop: "1px solid #ebedf2", margin: "20px 0" }} />

        <INDataTable
          columns={usersColumns}
          data={users}
          state={{ isLoading: loading }}
          enablePagination={users.length > 10}
          muiTableBodyRowProps={({ row }) => ({ key: row.original.id || row.original.oid })}
        />
      </MDBox>

      {/* ID211 — delete-user warning + confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "#b71c1c", fontWeight: 700 }}>Delete this user?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            This permanently removes <b>{deleteUserRow?.name || deleteUserRow?.email}</b> from this
            tenant — their access, role and category assignments. This cannot be undone. (Their
            login identity isn&apos;t deleted; you can re-invite them later to restore access.)
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            disabled={deleting}
            onClick={handleDeleteUser}
            sx={{ bgcolor: "#b71c1c", color: "#fff", "&:hover": { bgcolor: "#8e1414" } }}
          >
            {deleting ? "Deleting…" : "Delete user"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addOpen}
        onClose={() => {
          if (addLoading) return;
          setAddOpen(false);
          setCategoryAttachments([ALL_CATEGORIES_OPTION]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add user</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            required
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            select
            label="Role"
            fullWidth
            value={addRole}
            onChange={(e) => {
              const v = e.target.value;
              setAddRole(v);
              if (needsCategoryPicker(v)) setCategoryAttachments([ALL_CATEGORIES_OPTION]);
              else setCategoryAttachments([]);
            }}
            SelectProps={{ sx: { minHeight: 48 } }}
          >
            {ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          {needsCategoryPicker(addRole) && (
            <Autocomplete
              multiple
              options={[ALL_CATEGORIES_OPTION, ...categoriesForAssignment]}
              getOptionLabel={(opt) => opt.label || `${opt.taxonomyName} - ${opt.categoryName}`}
              isOptionEqualToValue={(opt, val) =>
                opt.categoryId === val.categoryId && opt.taxonomyId === val.taxonomyId
              }
              value={categoryAttachments}
              onChange={(_, newVal) => {
                const hadAll = categoryAttachments.some((v) => v.categoryId === "__ALL__");
                const hasAll = newVal.some((v) => v.categoryId === "__ALL__");
                if (!hadAll && hasAll) {
                  setCategoryAttachments([ALL_CATEGORIES_OPTION]);
                } else if (hadAll && hasAll && newVal.length > 1) {
                  setCategoryAttachments(newVal.filter((v) => v.categoryId !== "__ALL__"));
                } else if (newVal.length === 0) {
                  setCategoryAttachments([ALL_CATEGORIES_OPTION]);
                } else {
                  setCategoryAttachments(newVal);
                }
              }}
              loading={categoriesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label="Assigned categories"
                  placeholder={
                    categoryAttachments.some((v) => v.categoryId === "__ALL__")
                      ? ""
                      : "Select categories…"
                  }
                  helperText={
                    categoryAttachments.some((v) => v.categoryId === "__ALL__")
                      ? "All categories — no restrictions."
                      : addRole === "VIEW"
                      ? "User will only see themes linked to selected categories."
                      : "Owner can edit only the selected categories."
                  }
                />
              )}
              sx={{ mt: 0.5 }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                color="primary"
              />
            }
            label="Invite for login (send invite email now)"
            sx={{ mt: 1 }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            onClick={() => setAddOpen(false)}
            disabled={addLoading}
            sx={{ borderColor: "#B5BFD0", color: "#464d60" }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleAddSubmit}
            disabled={addLoading}
            sx={{
              backgroundColor: "#402899",
              color: "#fff",
              "&:hover": { backgroundColor: "#352080" },
              minWidth: 100,
            }}
          >
            {addLoading ? <CircularProgress size={20} color="inherit" /> : "Add user"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modifyOpen}
        onClose={() => {
          setModifyOpen(false);
          setModifyUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="First Name"
            fullWidth
            value={modifyFirstName}
            onChange={(e) => setModifyFirstName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            value={modifyLastName}
            onChange={(e) => setModifyLastName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={modifyUser?.email ?? ""}
            InputProps={{ readOnly: true }}
            helperText="Email is the sign-in identity and can't be changed here."
          />
          <TextField
            margin="dense"
            select
            label="Role"
            fullWidth
            value={modifyRole}
            onChange={(e) => setModifyRole(e.target.value)}
          >
            {ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          {needsCategoryPicker(modifyRole) && (
            <Autocomplete
              multiple
              options={[ALL_CATEGORIES_OPTION, ...categoriesForAssignment]}
              getOptionLabel={(opt) =>
                opt.label || `${opt.taxonomyName || ""} - ${opt.categoryName || ""}`
              }
              value={modifyCategoryAttachments.map((att) => {
                if (att.categoryId === "__ALL__") return ALL_CATEGORIES_OPTION;
                const match = categoriesForAssignment.find(
                  (opt) => opt.categoryId === att.categoryId && opt.taxonomyId === att.taxonomyId
                );
                return match || att;
              })}
              isOptionEqualToValue={(opt, val) =>
                opt.categoryId === val.categoryId && opt.taxonomyId === val.taxonomyId
              }
              onChange={(_, newVal) => {
                const hadAll = modifyCategoryAttachments.some((v) => v.categoryId === "__ALL__");
                const hasAll = newVal.some((v) => v.categoryId === "__ALL__");
                if (!hadAll && hasAll) {
                  setModifyCategoryAttachments([ALL_CATEGORIES_OPTION]);
                } else if (hadAll && hasAll && newVal.length > 1) {
                  setModifyCategoryAttachments(newVal.filter((v) => v.categoryId !== "__ALL__"));
                } else if (newVal.length === 0) {
                  setModifyCategoryAttachments([ALL_CATEGORIES_OPTION]);
                } else {
                  setModifyCategoryAttachments(newVal);
                }
              }}
              loading={categoriesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label="Assigned categories"
                  placeholder={
                    modifyCategoryAttachments.some((v) => v.categoryId === "__ALL__")
                      ? ""
                      : "Select categories…"
                  }
                  helperText={
                    modifyCategoryAttachments.some((v) => v.categoryId === "__ALL__")
                      ? "All categories — no restrictions."
                      : modifyUser?.role === "VIEW"
                      ? "User will only see themes linked to selected categories."
                      : "Owner can edit only the selected categories."
                  }
                />
              )}
              sx={{ mt: 1.5 }}
            />
          )}
          <MDBox sx={{ mt: 2 }}>
            {(modifyUser?.inviteStatus || "accepted") === "accepted" ? (
              <MDTypography variant="body2" color="text.secondary">
                This user has login access.
              </MDTypography>
            ) : (modifyUser?.inviteStatus || "none") === "pending" ? (
              <>
                <MDTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: modifyUser?.inviteExpired ? "#d32f2f" : "#8090AA",
                  }}
                >
                  {modifyUser?.inviteExpired
                    ? `Invite expired${
                        modifyUser?.inviteExpiresAt
                          ? ` on ${new Date(modifyUser.inviteExpiresAt).toLocaleString()}`
                          : ""
                      }. Resend to send a fresh 7-day link.`
                    : `Invite pending${
                        modifyUser?.inviteExpiresAt
                          ? ` — expires ${new Date(modifyUser.inviteExpiresAt).toLocaleString()}`
                          : ""
                      }.`}
                </MDTypography>
                <MDButton
                  variant="contained"
                  onClick={handleModifyInvite}
                  sx={{
                    backgroundColor: "#402899",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#352080" },
                  }}
                >
                  <Icon sx={{ mr: 0.5 }}>forward_to_inbox</Icon>
                  Resend invite
                </MDButton>
              </>
            ) : (
              <MDButton
                variant="contained"
                onClick={handleModifyInvite}
                sx={{
                  backgroundColor: "#402899",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#352080" },
                }}
              >
                <Icon sx={{ mr: 0.5 }}>mail</Icon>
                Invite for login
              </MDButton>
            )}
          </MDBox>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            onClick={() => {
              setModifyOpen(false);
              setModifyUser(null);
            }}
            sx={{ borderColor: "#B5BFD0", color: "#464d60" }}
          >
            Close
          </MDButton>
          <MDButton
            variant="contained"
            onClick={handleSaveCategories}
            sx={{
              backgroundColor: "#402899",
              color: "#fff",
              "&:hover": { backgroundColor: "#352080" },
            }}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default TenantUsers;
